import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container as ContainerIcon, Truck, Layers, PackageOpen, FileText, ShieldCheck,
  Warehouse, Download, ArrowRight, ClipboardList,
} from "lucide-react";

/**
 * DistributorCentre — professional wholesale/export section per brand.
 * Includes MOQ, 20ft & 40ft container loading, pallet configuration,
 * packaging specs, export documentation, wholesale info, catalogue &
 * spec-sheet download stubs, distributor application CTA, price-list CTA.
 */
const HS_CODES = {
  maltora: "2202.99.19",
  onecola: "2202.10.00",
  onefresh: "2009.90.11",
  onespring: "2201.10.11",
  voltx: "2202.99.15",
};

export default function DistributorCentre({ brand, onRequestPriceList }) {
  const specs = SPECS[brand.slug] || DEFAULT_SPECS;
  const hsCode = HS_CODES[brand.slug] || "2202.99.00";
  const leadTime = brand.slug === "onespring" ? "3–6 weeks" : "4–8 weeks";

  return (
    <section className="py-24 bg-neutral-950 text-white" data-testid={`distributor-centre-${brand.slug}`}>
      <div className="max-w-7xl mx-auto px-6">

        <header className="max-w-3xl mb-12">
          <div className="eyebrow text-gold-400 mb-3">Distributor Centre</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
            Wholesale &amp; Export — {brand.name}
          </h2>
          <p className="mt-4 text-neutral-300 leading-relaxed">
            Everything you need to plan container-load orders of {brand.name}: minimum order quantities,
            pallet configurations, container loading, packaging specifications, export documentation and
            wholesale contacts. Full commercial terms are provided on application.
          </p>
        </header>

        {/* Headline stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <StatTile Icon={ContainerIcon} label="MOQ" value={specs.moq} />
          <StatTile Icon={Truck} label="20ft Container" value={`${specs.container20.cases.toLocaleString()} cases`} />
          <StatTile Icon={Warehouse} label="40ft Container" value={`${specs.container40.cases.toLocaleString()} cases`} />
          <StatTile Icon={Layers} label="Cases / Pallet" value={`${specs.pallet.casesPerPallet}`} />
        </div>

        {/* Second row: HS code, lead time, carton dims, shelf life */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-12">
          <StatTile Icon={FileText} label="HS Code" value={hsCode} />
          <StatTile Icon={Truck} label="Lead Time" value={leadTime} />
          <StatTile Icon={PackageOpen} label="Carton Dimensions" value="395 × 265 × 145 mm" />
          <StatTile Icon={ShieldCheck} label="Shelf Life" value={specs.shelfLife.split("(")[0].trim()} />
        </div>

        {/* Loading tables */}
        <div className="grid lg:grid-cols-2 gap-4 mb-12">
          <LoadingCard title="20ft Container (FCL)" data={specs.container20} accent="#D4AF37" />
          <LoadingCard title="40ft Container (FCL)" data={specs.container40} accent="#0EA5E9" />
        </div>

        {/* Pallet configuration */}
        <div className="rounded-2xl bg-neutral-900 border border-white/10 p-6 lg:p-8 mb-4" data-testid="pallet-config">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="eyebrow text-gold-400">Pallet Configuration</div>
              <h3 className="font-display font-bold text-2xl mt-1">Optimised for road, sea &amp; ISO pallets</h3>
            </div>
            <span className="text-xs uppercase tracking-widest text-neutral-400 self-center">
              Euro pallet 1200 × 800 · ISO pallet 1200 × 1000
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-widest text-neutral-500">
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 pr-4 font-normal">Format</th>
                  <th className="text-left py-3 pr-4 font-normal">Case Config</th>
                  <th className="text-left py-3 pr-4 font-normal">Cases / Layer</th>
                  <th className="text-left py-3 pr-4 font-normal">Layers</th>
                  <th className="text-left py-3 pr-4 font-normal">Cases / Pallet</th>
                  <th className="text-left py-3 pr-4 font-normal">Units / Pallet</th>
                </tr>
              </thead>
              <tbody>
                {specs.palletTable.map((r, i) => (
                  <tr key={r.format + i} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="py-3 pr-4 font-semibold">{r.format}</td>
                    <td className="py-3 pr-4 text-neutral-300">{r.caseConfig}</td>
                    <td className="py-3 pr-4 text-neutral-300">{r.casesPerLayer}</td>
                    <td className="py-3 pr-4 text-neutral-300">{r.layers}</td>
                    <td className="py-3 pr-4 text-neutral-300">{r.casesPerPallet}</td>
                    <td className="py-3 pr-4 font-semibold text-gold-400">{r.unitsPerPallet.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Packaging + documentation grid */}
        <div className="grid lg:grid-cols-2 gap-4 mb-4">
          <InfoBlock Icon={PackageOpen} title="Packaging Specifications">
            <ul className="space-y-1.5 text-sm text-neutral-300">
              {specs.packaging.map((p) => <li key={p} className="flex gap-2"><span className="text-gold-400">›</span>{p}</li>)}
            </ul>
          </InfoBlock>

          <InfoBlock Icon={FileText} title="Export Documentation">
            <ul className="space-y-1.5 text-sm text-neutral-300">
              {EXPORT_DOCS.map((d) => <li key={d} className="flex gap-2"><span className="text-gold-400">›</span>{d}</li>)}
            </ul>
          </InfoBlock>
        </div>

        {/* Wholesale + certifications */}
        <div className="grid lg:grid-cols-2 gap-4 mb-4">
          <InfoBlock Icon={ShieldCheck} title="Certifications &amp; Compliance">
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-neutral-300">
              {CERTIFICATIONS.map((c) => <li key={c} className="flex gap-2"><span className="text-gold-400">›</span>{c}</li>)}
            </ul>
          </InfoBlock>

          <InfoBlock Icon={ClipboardList} title="Wholesale Information">
            <ul className="space-y-1.5 text-sm text-neutral-300">
              <li className="flex gap-2"><span className="text-gold-400">›</span>Incoterms: FCA Manchester (default) · FOB / CIF on request</li>
              <li className="flex gap-2"><span className="text-gold-400">›</span>Currencies: GBP, USD, EUR</li>
              <li className="flex gap-2"><span className="text-gold-400">›</span>Payment: 30% advance · 70% against B/L copy</li>
              <li className="flex gap-2"><span className="text-gold-400">›</span>Lead time: 4–8 weeks after PO confirmation</li>
              <li className="flex gap-2"><span className="text-gold-400">›</span>Shelf life: {specs.shelfLife} from production</li>
              <li className="flex gap-2"><span className="text-gold-400">›</span>Private label &amp; co-manufacturing available above 5 x 40ft FCL / year</li>
            </ul>
          </InfoBlock>
        </div>

        {/* Downloads */}
        <div className="rounded-2xl bg-neutral-900 border border-white/10 p-6 lg:p-8 mb-8" data-testid="distributor-downloads">
          <div className="eyebrow text-gold-400 mb-2">Downloads</div>
          <h3 className="font-display font-bold text-2xl mb-6">{brand.name} distributor pack</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {DOWNLOADS(brand).map((d) => (
              <a
                key={d.title}
                href={d.url}
                target="_blank" rel="noopener noreferrer"
                className="flex items-start gap-3 p-4 rounded-xl bg-black/40 border border-white/5 hover:border-gold-400/60 transition-colors group"
                data-testid={`download-${d.title.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="w-10 h-10 rounded-lg bg-gold-400/10 flex items-center justify-center flex-none group-hover:bg-gold-400/20 transition-colors">
                  <Download className="w-4 h-4 text-gold-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold group-hover:text-gold-400 transition-colors">{d.title}</div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">{d.type} · {d.size}</div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="rounded-3xl bg-gradient-to-br from-gold-400 via-amber-400 to-gold-600 p-8 lg:p-12 text-neutral-950"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold opacity-70">Next Step</div>
              <h3 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-2">
                Ready to distribute {brand.name}?
              </h3>
              <p className="mt-3 opacity-80 max-w-lg">
                Apply to become an authorised SBC distributor or request our confidential price list.
                Our export team responds within 5 business days.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/distributor"
                className="inline-flex items-center gap-2 bg-neutral-950 text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-neutral-800 transition-colors"
                data-testid="btn-apply-distributor"
              >
                Apply Now <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={onRequestPriceList}
                className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-neutral-950/20 hover:bg-white/40 text-neutral-950 rounded-full px-6 py-3 text-sm font-semibold transition-colors"
                data-testid="btn-request-price-list"
              >
                Request Price List
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Presentational sub-components ────────────────────────────────────
function StatTile({ Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-neutral-900 border border-white/10 p-5">
      <Icon className="w-5 h-5 text-gold-400" />
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-3">{label}</div>
      <div className="font-display font-bold text-xl mt-1">{value}</div>
    </div>
  );
}

function LoadingCard({ title, data, accent }) {
  return (
    <div className="rounded-2xl bg-neutral-900 border border-white/10 p-6" data-testid={`loading-${title.slice(0,4)}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-lg">{title}</h3>
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${accent}20`, color: accent }}>
          <Truck className="w-4 h-4" />
        </div>
      </div>
      <dl className="space-y-2 text-sm">
        {[
          ["Pallets per container", data.pallets],
          ["Cases per container", data.cases.toLocaleString()],
          ["Units per container", data.units.toLocaleString()],
          ["Approx. gross weight", data.weight],
          ["Cubic metres", data.cbm],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between py-1.5 border-b border-white/5 last:border-0">
            <dt className="text-neutral-400">{k}</dt>
            <dd className="font-semibold">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function InfoBlock({ Icon, title, children }) {
  return (
    <div className="rounded-2xl bg-neutral-900 border border-white/10 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-gold-400/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-gold-400" />
        </div>
        <h3 className="font-display font-bold text-lg">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ─── Data ─────────────────────────────────────────────────────────────
const EXPORT_DOCS = [
  "Commercial Invoice",
  "Packing List",
  "Bill of Lading / Airway Bill",
  "Certificate of Origin (UK Chamber of Commerce)",
  "Certificate of Analysis (batch-specific)",
  "Health / Free-Sale Certificate on request",
  "Halal / Kosher certificates on request",
  "Insurance Certificate (CIF orders)",
  "Phytosanitary / Fumigation on request",
];

const CERTIFICATIONS = [
  "ISO 22000 (Food Safety)",
  "HACCP",
  "FSSC 22000 aligned",
  "ISO 9001 (Quality)",
  "Halal Certified",
  "Kosher (on request)",
  "UK Chamber of Commerce",
  "GS1 barcoded",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOWNLOADS = (brand) => [
  { title: `${brand.name} Product Catalogue 2026`, type: "PDF", size: "Live", url: `${BACKEND_URL}/api/downloads/pdf/${brand.slug}/catalogue` },
  { title: `${brand.name} Specification Sheet`, type: "PDF", size: "Live", url: `${BACKEND_URL}/api/downloads/pdf/${brand.slug}/specification` },
  { title: `${brand.name} Nutrition Panel Pack`, type: "PDF", size: "Live", url: `${BACKEND_URL}/api/downloads/pdf/${brand.slug}/nutrition` },
  { title: `${brand.name} Technical Data Sheet`, type: "PDF", size: "Live", url: `${BACKEND_URL}/api/downloads/pdf/${brand.slug}/tech-data` },
  { title: `${brand.name} Distributor Brochure`, type: "PDF", size: "Live", url: `${BACKEND_URL}/api/downloads/pdf/${brand.slug}/distributor-brochure` },
  { title: `${brand.name} Export Catalogue`, type: "PDF", size: "Live", url: `${BACKEND_URL}/api/downloads/pdf/${brand.slug}/export-catalogue` },
];

const DEFAULT_SPECS = {
  moq: "1 x 20ft FCL",
  container20: { pallets: "20 Euro pallets", cases: 1400, units: 33600, weight: "~18 t", cbm: "28 m³" },
  container40: { pallets: "40 Euro pallets", cases: 2800, units: 67200, weight: "~24 t", cbm: "58 m³" },
  pallet: { casesPerPallet: 70 },
  shelfLife: "12 months",
  packaging: [
    "Primary: aluminium can / PET bottle / glass bottle",
    "Secondary: shrink-wrapped tray or corrugated case",
    "Tertiary: stretch-wrapped pallet, corner boards",
    "GS1 EAN-13 barcode per SKU",
    "Best-before + batch code laser-printed",
  ],
  palletTable: [
    { format: "250ml Can (24 x tray)", caseConfig: "24 cans", casesPerLayer: 15, layers: 6, casesPerPallet: 90, unitsPerPallet: 2160 },
    { format: "330ml Can (24 x tray)", caseConfig: "24 cans", casesPerLayer: 12, layers: 6, casesPerPallet: 72, unitsPerPallet: 1728 },
    { format: "500ml PET (12 x case)", caseConfig: "12 bottles", casesPerLayer: 10, layers: 6, casesPerPallet: 60, unitsPerPallet: 720 },
    { format: "1L PET (12 x case)", caseConfig: "12 bottles", casesPerLayer: 9, layers: 5, casesPerPallet: 45, unitsPerPallet: 540 },
    { format: "1.5L PET (6 x case)", caseConfig: "6 bottles", casesPerLayer: 9, layers: 4, casesPerPallet: 36, unitsPerPallet: 216 },
  ],
};

// Per-brand tweaks (values calibrated to typical industry loading rates)
const SPECS = {
  maltora: {
    ...DEFAULT_SPECS,
    moq: "1 x 20ft FCL",
    container20: { pallets: "20 Euro pallets", cases: 1300, units: 31200, weight: "~19 t", cbm: "28 m³" },
    container40: { pallets: "40 Euro pallets", cases: 2600, units: 62400, weight: "~26 t", cbm: "58 m³" },
    pallet: { casesPerPallet: 65 },
    shelfLife: "12 months",
    packaging: [
      "Primary: brown amber glass bottle (250/330/500ml), aluminium can, 1L PET",
      "Secondary: 24 x can tray · 12 x glass tray · 6 x PET case",
      "Halal certificate available",
      "GS1 EAN-13 barcode per SKU",
      "Best-before + batch code laser-printed",
    ],
    palletTable: [
      { format: "250ml Can", caseConfig: "24 cans", casesPerLayer: 15, layers: 6, casesPerPallet: 90, unitsPerPallet: 2160 },
      { format: "330ml Can", caseConfig: "24 cans", casesPerLayer: 12, layers: 6, casesPerPallet: 72, unitsPerPallet: 1728 },
      { format: "500ml Can", caseConfig: "12 cans", casesPerLayer: 12, layers: 6, casesPerPallet: 72, unitsPerPallet: 864 },
      { format: "330ml Glass", caseConfig: "24 bottles", casesPerLayer: 9, layers: 5, casesPerPallet: 45, unitsPerPallet: 1080 },
      { format: "500ml Glass", caseConfig: "12 bottles", casesPerLayer: 9, layers: 5, casesPerPallet: 45, unitsPerPallet: 540 },
      { format: "1L PET", caseConfig: "12 bottles", casesPerLayer: 9, layers: 5, casesPerPallet: 45, unitsPerPallet: 540 },
    ],
  },
  onecola: {
    ...DEFAULT_SPECS,
    moq: "1 x 20ft FCL",
    container20: { pallets: "20 Euro pallets", cases: 1500, units: 36000, weight: "~18 t", cbm: "28 m³" },
    container40: { pallets: "40 Euro pallets", cases: 3000, units: 72000, weight: "~24 t", cbm: "58 m³" },
    pallet: { casesPerPallet: 75 },
    shelfLife: "12 months",
    packaging: DEFAULT_SPECS.packaging,
    palletTable: [
      { format: "250ml Can", caseConfig: "24 cans", casesPerLayer: 15, layers: 6, casesPerPallet: 90, unitsPerPallet: 2160 },
      { format: "330ml Can", caseConfig: "24 cans", casesPerLayer: 12, layers: 6, casesPerPallet: 72, unitsPerPallet: 1728 },
      { format: "500ml Can", caseConfig: "12 cans", casesPerLayer: 12, layers: 6, casesPerPallet: 72, unitsPerPallet: 864 },
      { format: "500ml PET", caseConfig: "12 bottles", casesPerLayer: 10, layers: 6, casesPerPallet: 60, unitsPerPallet: 720 },
      { format: "1L PET", caseConfig: "12 bottles", casesPerLayer: 9, layers: 5, casesPerPallet: 45, unitsPerPallet: 540 },
      { format: "1.5L PET", caseConfig: "6 bottles", casesPerLayer: 9, layers: 4, casesPerPallet: 36, unitsPerPallet: 216 },
      { format: "2L PET", caseConfig: "6 bottles", casesPerLayer: 8, layers: 4, casesPerPallet: 32, unitsPerPallet: 192 },
    ],
  },
  onefresh: {
    ...DEFAULT_SPECS,
    moq: "1 x 20ft FCL",
    container20: { pallets: "20 Euro pallets", cases: 1350, units: 32400, weight: "~19 t", cbm: "28 m³" },
    container40: { pallets: "40 Euro pallets", cases: 2700, units: 64800, weight: "~26 t", cbm: "58 m³" },
    pallet: { casesPerPallet: 68 },
    shelfLife: "9 months",
    packaging: [
      "Primary: PET juice bottle (330ml, 500ml, 1.5L), aseptic carton (250ml, 1L)",
      "Secondary: 12 x PET case · 24 x carton case",
      "No artificial preservatives · Vitamin C fortified",
      "GS1 EAN-13 barcode per SKU",
      "Best-before + batch code laser-printed",
    ],
    palletTable: [
      { format: "250ml Carton", caseConfig: "24 packs", casesPerLayer: 15, layers: 6, casesPerPallet: 90, unitsPerPallet: 2160 },
      { format: "330ml PET", caseConfig: "12 bottles", casesPerLayer: 12, layers: 6, casesPerPallet: 72, unitsPerPallet: 864 },
      { format: "500ml PET", caseConfig: "12 bottles", casesPerLayer: 10, layers: 6, casesPerPallet: 60, unitsPerPallet: 720 },
      { format: "1L Carton", caseConfig: "12 packs", casesPerLayer: 10, layers: 5, casesPerPallet: 50, unitsPerPallet: 600 },
      { format: "1.5L PET", caseConfig: "6 bottles", casesPerLayer: 9, layers: 4, casesPerPallet: 36, unitsPerPallet: 216 },
    ],
  },
  onespring: {
    ...DEFAULT_SPECS,
    moq: "1 x 20ft FCL",
    container20: { pallets: "20 Euro pallets", cases: 1600, units: 38400, weight: "~22 t", cbm: "28 m³" },
    container40: { pallets: "40 Euro pallets", cases: 3200, units: 76800, weight: "~28 t", cbm: "58 m³" },
    pallet: { casesPerPallet: 80 },
    shelfLife: "18 months (still) · 12 months (sparkling & flavoured)",
    packaging: [
      "Primary: recyclable PET (330ml – 10L) · returnable 19L PC dispenser",
      "Secondary: 12/24 x shrink-wrap tray",
      "Natural mineral & sparkling variants use CO₂-safe packaging",
      "GS1 EAN-13 barcode per SKU",
      "Batch code + production date on cap",
    ],
    palletTable: [
      { format: "330ml PET", caseConfig: "24 bottles", casesPerLayer: 12, layers: 7, casesPerPallet: 84, unitsPerPallet: 2016 },
      { format: "500ml PET", caseConfig: "24 bottles", casesPerLayer: 10, layers: 6, casesPerPallet: 60, unitsPerPallet: 1440 },
      { format: "750ml PET", caseConfig: "12 bottles", casesPerLayer: 10, layers: 6, casesPerPallet: 60, unitsPerPallet: 720 },
      { format: "1.5L PET", caseConfig: "6 bottles", casesPerLayer: 9, layers: 4, casesPerPallet: 36, unitsPerPallet: 216 },
      { format: "5L PET", caseConfig: "4 bottles", casesPerLayer: 6, layers: 3, casesPerPallet: 18, unitsPerPallet: 72 },
      { format: "10L PET", caseConfig: "2 bottles", casesPerLayer: 6, layers: 2, casesPerPallet: 12, unitsPerPallet: 24 },
      { format: "19L Dispenser", caseConfig: "1 unit", casesPerLayer: 4, layers: 1, casesPerPallet: 4, unitsPerPallet: 4 },
    ],
  },
  voltx: {
    ...DEFAULT_SPECS,
    moq: "1 x 20ft FCL",
    container20: { pallets: "20 Euro pallets", cases: 1500, units: 36000, weight: "~17 t", cbm: "28 m³" },
    container40: { pallets: "40 Euro pallets", cases: 3000, units: 72000, weight: "~23 t", cbm: "58 m³" },
    pallet: { casesPerPallet: 75 },
    shelfLife: "12 months",
    packaging: [
      "Primary: slim aluminium energy can (250ml, 330ml, 500ml)",
      "Secondary: 24 x can tray shrink-wrap",
      "B-Vitamins, taurine and electrolytes declared per country",
      "GS1 EAN-13 barcode per SKU",
      "Best-before + batch code laser-printed",
    ],
    palletTable: [
      { format: "250ml Slim Can", caseConfig: "24 cans", casesPerLayer: 15, layers: 7, casesPerPallet: 105, unitsPerPallet: 2520 },
      { format: "330ml Can", caseConfig: "24 cans", casesPerLayer: 12, layers: 6, casesPerPallet: 72, unitsPerPallet: 1728 },
      { format: "500ml Can", caseConfig: "12 cans", casesPerLayer: 12, layers: 6, casesPerPallet: 72, unitsPerPallet: 864 },
    ],
  },
};
